import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { SessionExpiredModal } from '@/components/SessionExpiredModal';
import { clearListMemory } from '@/lib/listMemory';
import {
  call,
  clearSessionExpired,
  onSessionExpired,
  storeAccessToken,
  tokenExists,
} from '@/lib/api';

type TInfo = {
  id: string;
  uuid: string;
  email: string;
  info: {
    name: string;
    picture: string;
  };
  mondayToken: string;
  createdAt: string;
};

export const getUserInfo = async () =>
  call('user/info').then((res) => res.json() as Promise<TInfo>);

type UserContext = {
  loading: boolean;
  user: TInfo | null;
  isAuthenticated: boolean;
  /** The session died while the app was open and the user must sign in again. */
  sessionExpired: boolean;
  signIn: (credential: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const ctx = createContext<UserContext>({} as UserContext);

export const useAuth = () => useContext<UserContext>(ctx);

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<TInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setAuthenticated] = useState(tokenExists());
  const [sessionExpired, setSessionExpired] = useState(false);

  // The session can die at any moment while the app is open — the refresh token simply
  // reaches its limit. Nothing used to notice, so the shell kept rendering as signed in
  // and every action failed silently. This is the app's one place to hear about it.
  useEffect(() => {
    const unsubscribe = onSessionExpired(() => setSessionExpired(true));
    return () => {
      unsubscribe();
    };
  }, []);

  const signIn = (credential: string) =>
    call('user/sign-in', { json: { credential } }, true).then(async (res) => {
      storeAccessToken(await res.text());
      clearSessionExpired();

      const next = await getUserInfo();

      // Signing back in as somebody else must not drop them onto the previous person's
      // screen, with that person's records still cached behind the dialog. Start clean.
      if (user && next.uuid !== user.uuid) {
        window.location.reload();
        return;
      }

      // Recovering mid-session: `isAuthenticated` is already true, so the effect below
      // will not re-run. Set the user here or the app keeps the pre-expiry copy.
      setUser(next);
      setSessionExpired(false);
      setAuthenticated(true);
    });

  const signOut = () =>
    // Sent unauthenticated on purpose. Going through the authenticated path would try to
    // rotate first, and a session already dead cannot rotate — so the one request that
    // clears the stale cookie would never be sent, which is the state we are escaping.
    call('user/sign-out', {}, true)
      // And signing out must succeed for the user even if the request does not.
      .catch(() => undefined)
      .then(() => {
        storeAccessToken(null);
        clearListMemory();
        clearSessionExpired();
        setSessionExpired(false);
        setAuthenticated(false);
        // Clearing the user is what actually returns them to the sign-in screen: App
        // renders the app shell whenever a user is present, so leaving it behind kept
        // someone who had just logged out sitting inside the application. It also stops
        // a request still in flight raising the expired-session dialog over the top of
        // an intentional sign-out.
        setUser(null);
      });

  useEffect(() => {
    if (!isAuthenticated) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sets loading flag before async user fetch
    setLoading(true);
    getUserInfo()
      .then(setUser)
      .catch(() => {
        storeAccessToken(null);
        setAuthenticated(false);
      })
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  return (
    <ctx.Provider
      value={{
        loading,
        user,
        isAuthenticated,
        sessionExpired,
        signIn,
        signOut,
      }}
    >
      {children}
      {/* Rendered over whatever the user was doing rather than replacing it, so an
          expired session costs them their place but never their unsaved work. */}
      {sessionExpired && user && <SessionExpiredModal onSignIn={signIn} />}
    </ctx.Provider>
  );
}
